const DEFAULT_MATCH_TYPE = 'prefix';

const normalizeTitle = (value = '') =>
  String(value)
    .replace(/\s+/g, ' ')
    .replace(/\s*:\s*/g, ': ')
    .replace(/\bAPPENDIX:\s*/gi, 'APPENDIX ')
    .replace(/\s*\.\s*/g, '.')
    .replace(/[.…]+/g, '.')
    .replace(/\.\s*(?=\d)/g, '.')
    .replace(/\.$/, '')
    .trim()
    .toUpperCase();

const extractTitleFromSection = (section) => {
  if (!section) return '';
  if (typeof section === 'string') return section;
  if (typeof section.title === 'string') return section.title;
  if (typeof section.name === 'string') return section.name;
  return '';
};

const extractNumberingSequence = (title = '') => {
  const match = String(title).match(/^(\d+(?:\.\d+)*)/);
  return match ? match[1] : null;
};

const compareNumbering = (a, b) => {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i += 1) {
    const aVal = aParts[i] ?? 0;
    const bVal = bParts[i] ?? 0;
    if (aVal !== bVal) {
      return aVal - bVal;
    }
  }
  return aParts.length - bParts.length;
};

const validateSubsectionHierarchy = (sections = []) => {
  const numberingMap = new Map();

  sections.forEach((section) => {
    const title = extractTitleFromSection(section);
    const numbering = extractNumberingSequence(title);
    if (!numbering) {
      return;
    }

    if (!numberingMap.has(numbering)) {
      numberingMap.set(numbering, []);
    }
    numberingMap.get(numbering).push(title);
  });

  const sortedNumbers = Array.from(numberingMap.keys()).sort(compareNumbering);
  const errors = [];
  const hierarchyExemptions = new Set(['1.2', '1.3']);

  numberingMap.forEach((titles, numbering) => {
    if (titles.length > 1) {
      errors.push(
        `Section numbering "${numbering}" appears multiple times: ${titles.join(', ')}. Please remove duplicates before exporting.`
      );
    }
  });

  sortedNumbers.forEach((current) => {
    if (!current.includes('.')) {
      return;
    }

    const parts = current.split('.');
    const lastIndex = parts.length - 1;
    const expectedBase = parts.slice(0, lastIndex).join('.');
    const currentIndex = Number(parts[lastIndex]);
    const expectedPrevious = `${expectedBase}.${currentIndex - 1}`;

    if (hierarchyExemptions.has(current) || hierarchyExemptions.has(expectedPrevious)) {
      return;
    }

    if (currentIndex > 1 && !numberingMap.has(expectedPrevious)) {
      const currentTitles = numberingMap.get(current) || [];
      currentTitles.forEach((title) => {
        errors.push(`Section "${title}" is out of sequence. Expected subsection "${expectedPrevious}" before it.`);
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const extractTableEntries = (sections = []) => {
  const entries = [];
  sections.forEach((section) => {
    const title = extractTitleFromSection(section);
    const rawContent = extractContentFromSection(section);
    if (!rawContent) {
      return;
    }

    const sanitizedContent = rawContent.replace(/Table\s+of\s+Contents/gi, '');
    const regex = /(^|[^a-zA-Z])(Table)\s+(\d+)(?=[\s.:])/gi;
    let match;
    while ((match = regex.exec(sanitizedContent)) !== null) {
      const number = Number.parseInt(match[3], 10);
      if (Number.isNaN(number)) {
        continue;
      }

      entries.push({
        number,
        sectionTitle: title,
      });
    }
  });

  return entries;
};

const validateTableNumbering = (sections = []) => {
  const entries = extractTableEntries(sections);
  if (entries.length === 0) {
    return { isValid: true, errors: [] };
  }

  const errors = [];
  let expected = 1;

  entries.forEach((entry) => {
    if (entry.number !== expected) {
      errors.push(
        `Table numbering is out of sequence. Found "Table ${entry.number}" in section "${entry.sectionTitle}" but expected "Table ${expected}".`
      );
      expected = entry.number + 1;
    } else {
      expected += 1;
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const OBJECTIVE_SEQUENCE = [
  { key: 'PRIMARY', keyword: 'PRIMARY OBJECTIVE' },
  { key: 'SECONDARY', keyword: 'SECONDARY OBJECTIVE' },
  { key: 'EXPLORATORY', keyword: 'EXPLORATORY OBJECTIVE' },
  { key: 'OTHER', keyword: 'OTHER OBJECTIVE' },
];

const validateObjectiveHierarchy = (sections = []) => {
  const positions = new Map();

  sections.forEach((section, index) => {
    const title = extractTitleFromSection(section);
    if (!title) {
      return;
    }
    const normalizedTitle = normalizeTitle(title);
    OBJECTIVE_SEQUENCE.forEach(({ key, keyword }) => {
      if (normalizedTitle.includes(keyword)) {
        if (!positions.has(key)) {
          positions.set(key, []);
        }
        positions.get(key).push({ index, title });
      }
    });
  });

  const errors = [];

  for (let i = 0; i < OBJECTIVE_SEQUENCE.length; i += 1) {
    const current = OBJECTIVE_SEQUENCE[i];
    if (!positions.has(current.key)) {
      continue;
    }

    for (let j = 0; j < i; j += 1) {
      const previous = OBJECTIVE_SEQUENCE[j];
      if (!positions.has(previous.key)) {
        const items = positions.get(current.key) || [];
        items.forEach(({ title }) => {
          errors.push(
            `Section "${title}" violates the objectives hierarchy. Expected a ${previous.key.toLowerCase()} objective section before it.`
          );
        });
        break;
      }
      const earliestPrevious = positions.get(previous.key)[0]?.index ?? -1;
      const currentEntries = positions.get(current.key) || [];
      currentEntries.forEach(({ index: currentIndex, title }) => {
        if (currentIndex < earliestPrevious) {
          errors.push(
            `Section "${title}" appears before ${previous.key.toLowerCase()} objective sections, breaking the objectives hierarchy.`
          );
        }
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const extractContentFromSection = (section) => {
  if (!section) return '';
  if (typeof section.output === 'string') return section.output;
  if (typeof section.content === 'string') return section.content;
  if (typeof section.body === 'string') return section.body;
  return '';
};

const stripHtml = (value = '') =>
  String(value)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const evaluateSubsectionContent = (section) => {
  const title = extractTitleFromSection(section);
  const normalizedTitle = normalizeTitle(title);
  const rawContent = extractContentFromSection(section);
  const textContent = stripHtml(rawContent);

  const explicitNotProvidedInTitle = /\b(not provided|not applicable|n\/a)\b/i.test(title);
  const explicitNotProvidedInContent = /\b(not provided|not applicable|n\/a|no data)\b/i.test(textContent);

  const hasEmbeddedMedia = /<(img|table|figure|ul|ol|li|video|svg)\b/i.test(rawContent);
  const hasTextContent = textContent.length > 0;
  const hasAnyContent = hasTextContent || hasEmbeddedMedia;

  const requiresNotProvidedLabel = !hasAnyContent && !explicitNotProvidedInTitle && !explicitNotProvidedInContent;

  return {
    title,
    normalizedTitle,
    hasAnyContent,
    explicitNotProvidedInTitle,
    explicitNotProvidedInContent,
    requiresNotProvidedLabel,
  };
};

const REQUIRED_SECTION_LABELS = Object.freeze([
  '1. PROTOCOL SUMMARY',
  '1.2 TRIAL SCHEMA',
  '1.3 SCHEDULE OF ACTIVITIES',
  '2. INTRODUCTION',
  '3. TRIAL OBJECTIVES AND ASSOCIATED ESTIMANDS',
  '4. TRIAL DESIGN',
  '5. TRIAL POPULATION',
  '6. TRIAL INTERVENTION AND CONCOMITANT THERAPY',
  '7. PARTICIPANT DISCONTINUATION OF TRIAL INTERVENTION AND DISCONTINUATION OR WITHDRAWAL FROM TRIAL',
  '8. TRIAL ASSESSMENTS AND PROCEDURES',
  '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
  '10. STATISTICAL CONSIDERATIONS',
  '11. TRIAL OVERSIGHT AND OTHER GENERAL CONSIDERATIONS',
  '12. APPENDIX: SUPPORTING DETAILS',
  '13. APPENDIX: GLOSSARY OF TERMS AND ABBREVIATIONS',
  '14. APPENDIX: REFERENCES',
]);

const REQUIRED_M11_SECTION_RULES = REQUIRED_SECTION_LABELS.map((label) => ({
  label,
  matchType: DEFAULT_MATCH_TYPE,
}));

export const getMissingM11Sections = (sections) => {
  const sectionList = Array.isArray(sections) ? sections : [];

  const missing = [];

  REQUIRED_M11_SECTION_RULES.forEach((rule) => {
    const expected = normalizeTitle(rule.label);
    const expectedNumbering = extractNumberingSequence(rule.label);

    const isPresent = sectionList.some((section) => {
      const title = extractTitleFromSection(section);
      const normalized = normalizeTitle(title);
      const numbering = extractNumberingSequence(title);

      if (normalized.startsWith(expected)) {
        return true;
      }

      // Accept canonical M11 sections when only the appendix punctuation differs.
      if (expectedNumbering && numbering === expectedNumbering) {
        return true;
      }

      return false;
    });

    if (!isPresent) {
      missing.push(rule.label);
    }
  });

  return missing;
};

export const hasAllRequiredM11Sections = (sections) => {
  const missing = getMissingM11Sections(sections);
  return {
    isValid: missing.length === 0,
    missing,
  };
};

const normalizeFontFamily = (rawFamily = '') => {
  return rawFamily.replace(/["']/g, '').split(',')[0].trim().toLowerCase();
};

const parseFontSizeValue = (value = '') => {
  const match = String(value).toLowerCase().match(/([\d.]+)/);
  return match ? parseFloat(match[1]) : null;
};

export const validateFontComplianceForExport = (sections = []) => {
  const errors = [];
  if (!Array.isArray(sections) || sections.length === 0) {
    return { isValid: true, errors };
  }

  const REQUIRED_FAMILY = 'times new roman';
  const ALLOWED_SIZES = [9, 10, 11, 12, 14, 18];
  const fontFamilyRegex = /font-family\s*:\s*([^;"']+)[;"']?/gi;
  const fontSizeRegex = /font-size\s*:\s*([^;"']+)[;"']?/gi;

  const scanSectionHtml = (section) => {
    if (!section?.title) {
      return;
    }

    const sectionNumberMatch = section.title.match(/^(\d+)/);
    if (!sectionNumberMatch || Number.parseInt(sectionNumberMatch[1], 10) < 1) {
      return;
    }

    const content = section.output || section.content || '';
    if (content) {

    fontFamilyRegex.lastIndex = 0;
    fontSizeRegex.lastIndex = 0;

    const invalidFamilies = new Set();
    const invalidSizes = new Set();

    let familyMatch;
    while ((familyMatch = fontFamilyRegex.exec(content)) !== null) {
      const normalized = normalizeFontFamily(familyMatch[1]);
      if (normalized && !normalized.includes(REQUIRED_FAMILY)) {
        invalidFamilies.add(familyMatch[1].trim());
      }
    }

    let sizeMatch;
    while ((sizeMatch = fontSizeRegex.exec(content)) !== null) {
      const numericSize = parseFontSizeValue(sizeMatch[1]);
      if (numericSize === null) {
        invalidSizes.add(sizeMatch[1].trim());
        continue;
      }

      const matchesTolerance = ALLOWED_SIZES.some(
        (allowed) => Math.abs(numericSize - allowed) < 0.25
      );

      if (!matchesTolerance) {
        invalidSizes.add(sizeMatch[1].trim());
      }
    }

    if (invalidFamilies.size > 0 || invalidSizes.size > 0) {
      const problems = [];
      if (invalidFamilies.size > 0) {
        problems.push(`font-family values [${Array.from(invalidFamilies).join(', ')}]`);
      }
      if (invalidSizes.size > 0) {
        problems.push(`font-size values [${Array.from(invalidSizes).join(', ')}]`);
      }
      errors.push(
       `Section "${section.title}" contains ${problems.join(' and ')}. ` +
            'Expected Times New Roman where inline font-family is set, and font-size in the Draft 1.0 §4.1 set ' +
            '(9, 10, 11, 12, 14, or 18 pt — e.g. body 12 pt, main heading 14 pt, subordinate headings/tables 11 pt, footnotes/headers 10 pt, title 18 pt).',
        );
      }
    }
    (section.subsections || []).forEach(scanSectionHtml);
  };

  sections.forEach(scanSectionHtml);

  return { isValid: errors.length === 0, errors };
};

export const validateOptionalSectionsForExport = (sections = []) => {
  const issues = [];

  if (!Array.isArray(sections) || sections.length === 0) {
    return {
      isValid: true,
      issues,
      errors: [],
    };
  }

  sections.forEach((section) => {
    const title = extractTitleFromSection(section);
    if (!title) {
      return;
    }

    const subsectionNumberMatch = title.match(/^(\d+\.\d+(?:\.\d+)*)/);
    if (!subsectionNumberMatch) {
      return;
    }

    const evaluation = evaluateSubsectionContent(section);
    if (evaluation.requiresNotProvidedLabel) {
      issues.push(`Section "${title}" is detected as optional without content. Please append "(Not Provided)" to the title.`);
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
    errors: issues,
  };
};

export const validateSubsectionHierarchyForExport = (sections = []) =>
  validateSubsectionHierarchy(sections);

export const validateTableNumberingForExport = (sections = []) =>
  validateTableNumbering(sections);

export const validateObjectiveHierarchyForExport = (sections = []) =>
  validateObjectiveHierarchy(sections);

const validateSoALandscape = (sections = [], generatedSoAData = null) => {
  const errors = [];
  const matchingSections = (Array.isArray(sections) ? sections : []).filter((section) => {
    const title = extractTitleFromSection(section);
    return /SCHEDULE OF ACTIVITIES|\bSOA\b/i.test(title);
  });

  if (matchingSections.length === 0) {
    errors.push('Schedule of Activities section not found. Export requires an SOA section rendered in landscape.');
    return { isValid: false, errors };
  }

  if (matchingSections.length > 1) {
    errors.push('Multiple Schedule of Activities sections detected. Please keep exactly one SOA section to preserve landscape layout.');
  }

  // Accept every shape the Word/PDF export can render: legacy { visits }, the
  // USDM { tables } payload and S9 { s9: { tables } } (see normalizeSoaExportPayload).
  const nonEmpty = (arr) => Array.isArray(arr) && arr.length > 0;
  const hasStructuredSoAData = Boolean(
    generatedSoAData &&
      (nonEmpty(generatedSoAData.visits) ||
        nonEmpty(generatedSoAData.tables) ||
        nonEmpty(generatedSoAData.s9?.tables)),
  );

  if (!hasStructuredSoAData) {
    errors.push('Structured SOA data is missing. Please generate the Schedule of Activities before exporting to ensure landscape layout.');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateSoALandscapeForExport = (sections = [], generatedSoAData = null) =>
  validateSoALandscape(sections, generatedSoAData);

/**
 * Validate consistent SI units and symbols across text and tables
 * Checks for:
 * - Proper SI unit symbols (kg, g, mg, mL, L, m, cm, mm, km, etc.)
 * - Consistency of units across the document
 * - Common errors (kgs vs kg, meters vs m, etc.)
 */
const validateSIUnitsConsistency = (sections = []) => {
  const errors = [];
  const unitPatterns = {
    // Mass units
    mass: {
      valid: /\b(mg|g|kg|µg|ng|pg)\b/gi,
      invalid: /\b(kgs|grams|kilogram|kilograms|milligrams|micrograms)\b/gi,
      examples: { valid: 'kg, g, mg', invalid: 'kgs, grams, kilograms' }
    },
    // Volume units
    volume: {
      valid: /\b(mL|L|µL|nL|pL|ml|l)\b/gi,
      invalid: /\b(milliliters|millilitres|liters|litres|microliters|microlitres)\b/gi,
      examples: { valid: 'L, mL, µL', invalid: 'liters, milliliters' }
    },
    // Length units
    length: {
      valid: /\b(mm|cm|m|km|µm|nm|pm)\b/gi,
      invalid: /\b(meters|metres|centimeters|centimetres|millimeters|millimetres|kilometers|kilometres)\b/gi,
      examples: { valid: 'm, cm, mm, km', invalid: 'meters, centimeters' }
    },
    // Time units
    // Note: In clinical protocols, words like "days", "hours", "minutes" are commonly used and acceptable
    // We only flag if there's mixing of SI symbols with word forms in the same context
    time: {
      valid: /\b(s|min|h|d|ms|µs|ns)\b/gi,
      invalid: /\b(milliseconds|microseconds)\b/gi, // Only flag technical terms, not common words
      examples: { valid: 's, min, h, d', invalid: 'milliseconds, microseconds' }
    },
    // Temperature
    temperature: {
      valid: /\b(°C|K|°F)\b/gi,
      invalid: /\b(celsius|fahrenheit|kelvin|degrees)\b/gi,
      examples: { valid: '°C, K', invalid: 'celsius, fahrenheit' }
    },
    // Concentration
    concentration: {
      valid: /\b(mg\/mL|g\/L|µg\/mL|ng\/mL|mol\/L|M|mM|µM|nM|pM)\b/gi,
      invalid: /\b(mg per ml|grams per liter|milligrams per milliliter)\b/gi,
      examples: { valid: 'mg/mL, g/L, M, mM', invalid: 'mg per ml, grams per liter' }
    },
    // Dose/Amount
    dose: {
      valid: /\b(mg\/kg|g\/kg|µg\/kg|ng\/kg|mg\/m²|g\/m²)\b/gi,
      invalid: /\b(mg per kg|grams per kilogram|milligrams per kilogram)\b/gi,
      examples: { valid: 'mg/kg, g/kg, mg/m²', invalid: 'mg per kg, grams per kilogram' }
    }
  };

  const foundUnits = new Map(); // Track units found in each section
  const invalidUnits = []; // Track invalid unit usage

  if (!Array.isArray(sections) || sections.length === 0) {
    return { isValid: true, errors: [] };
  }

  sections.forEach((section) => {
    if (!section?.output) return;
    
    const content = section.output;
    const title = extractTitleFromSection(section);
    const sectionKey = title || 'Unknown Section';

    // Check for invalid units (non-SI format)
    Object.entries(unitPatterns).forEach(([category, patterns]) => {
      const invalidMatches = content.match(patterns.invalid);
      if (invalidMatches) {
        invalidMatches.forEach(match => {
          const invalidUnit = {
            section: sectionKey,
            unit: match,
            category,
            suggestion: patterns.examples.valid
          };
          if (!invalidUnits.some(u => u.section === invalidUnit.section && u.unit === invalidUnit.unit)) {
            invalidUnits.push(invalidUnit);
          }
        });
      }
    });

    // Extract valid SI units from content (text and tables)
    Object.entries(unitPatterns).forEach(([category, patterns]) => {
      const validMatches = content.match(patterns.valid);
      if (validMatches) {
        validMatches.forEach(match => {
          const normalizedUnit = match.toLowerCase();
          if (!foundUnits.has(normalizedUnit)) {
            foundUnits.set(normalizedUnit, []);
          }
          foundUnits.get(normalizedUnit).push({
            section: sectionKey,
            category
          });
        });
      }
    });
  });

  // Report invalid units
  invalidUnits.forEach(({ section, unit, category, suggestion }) => {
    errors.push(
      `Section "${section}" uses non-SI unit "${unit}" (${category}). Use SI symbols: ${suggestion}.`
    );
  });

  // Check for inconsistent unit usage (same measurement using different units)
  // Note: Time units (days, hours, minutes) are commonly used in clinical protocols and are acceptable
  // We only check for mixing of SI symbols with word forms for non-time units
  const unitVariations = {
    'kg': ['kilogram', 'kilograms', 'kgs'],
    'g': ['gram', 'grams'],
    'mg': ['milligram', 'milligrams'],
    'm': ['meter', 'meters', 'metre', 'metres'],
    'cm': ['centimeter', 'centimeters', 'centimetre', 'centimetres'],
    'mm': ['millimeter', 'millimeters', 'millimetre', 'millimetres'],
    'l': ['liter', 'liters', 'litre', 'litres'],
    'ml': ['milliliter', 'milliliters', 'millilitre', 'millilitres']
    // Note: Time units (d, h, min, s) with their word forms (days, hours, minutes, seconds) are acceptable
    // and commonly used in clinical protocols, so we don't flag them
  };

  // Check for mixed usage of units and their word forms (excluding time units)
  sections.forEach((section) => {
    if (!section?.output) return;
    const content = section.output.toLowerCase();
    const title = extractTitleFromSection(section);
    // A glossary/list of abbreviations defines the symbol by its word form
    // ("mg — milligram"); that is the expected place for both.
    if (/glossary|abbreviation/i.test(title)) return;

    Object.entries(unitVariations).forEach(([siUnit, wordForms]) => {
      const hasSI = new RegExp(`\\b${siUnit}\\b`, 'i').test(content);
      const hasWordForm = wordForms.some(form => new RegExp(`\\b${form}\\b`, 'i').test(content));
      
      if (hasSI && hasWordForm) {
        errors.push(
          `Section "${title}" mixes SI symbol "${siUnit}" with word form "${wordForms[0]}". Use consistent SI symbols throughout.`
        );
      }
    });
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateSIUnitsConsistencyForExport = (sections = []) =>
  validateSIUnitsConsistency(sections);

export default {
  REQUIRED_M11_SECTION_RULES,
  getMissingM11Sections,
  hasAllRequiredM11Sections,
  validateOptionalSectionsForExport,
  validateSubsectionHierarchyForExport,
  validateTableNumberingForExport,
  validateObjectiveHierarchyForExport,
  validateSoALandscapeForExport,
  validateSIUnitsConsistencyForExport,
};

